import { z } from 'zod'
import { ENTIDADE_CAMPO, TIPO_CAMPO, type EntidadeCampo, type TipoCampo } from '../enums'
import { textoObrigatorio } from './comum'

/**
 * Campos customizados (seção 3). O esquema fica em `campos_definicao` e o
 * valor em `contatos.campos`, jsonb com índice GIN. Desde a ADR 0069 a empresa
 * tem os dela, em `empresas.campos`: o campo diz de quem é pela `entidade`, e
 * cada um vira uma coluna na lista de Contatos ou de Empresas.
 *
 * `anexo` está fora da v1: a especificação não define onde o arquivo mora nem
 * como ele entra no XLSX de importação (dúvida B12). O tipo continua no enum
 * para não mexer no banco depois.
 */
export const TIPOS_CAMPO_DISPONIVEIS = TIPO_CAMPO.filter((t) => t !== 'anexo')

export const ROTULO_TIPO_CAMPO: Record<TipoCampo, string> = {
  texto: 'Texto',
  numero: 'Número',
  data: 'Data',
  moeda: 'Moeda',
  lista: 'Lista (escolha uma)',
  multipla_escolha: 'Múltipla escolha',
  checkbox: 'Sim/Não',
  anexo: 'Anexo (fora da v1)',
}

/** Chave usada dentro do jsonb: sem espaço, sem acento, para não virar dor. */
export const chaveCampoSchema = z
  .string()
  .trim()
  .min(1, 'chave é obrigatória')
  .max(60)
  .regex(/^[a-z][a-z0-9_]*$/, 'use só letras minúsculas, números e underline, começando por letra')

const opcoesSchema = z.array(z.string().trim().min(1)).min(1, 'informe ao menos uma opção')

export const criarCampoSchema = z
  .object({
    nome: textoObrigatorio('nome do campo', 80),
    chave: chaveCampoSchema,
    tipo: z.enum(TIPOS_CAMPO_DISPONIVEIS as unknown as [TipoCampo, ...TipoCampo[]]),
    entidade: z.enum(ENTIDADE_CAMPO).default('contato'),
    obrigatorio: z.boolean().default(false),
    opcoes: opcoesSchema.optional(),
    ordem: z.number().int().min(0).default(0),
  })
  .refine((c) => !precisaDeOpcoes(c.tipo) || (c.opcoes?.length ?? 0) > 0, {
    message: 'este tipo precisa de opções',
    path: ['opcoes'],
  })
export type CriarCampoEntrada = z.input<typeof criarCampoSchema>

/** `?entidade=empresa` lista os da empresa; sem nada, os do contato, como sempre foi. */
export const filtroCamposSchema = z.object({
  entidade: z.enum(ENTIDADE_CAMPO).default('contato'),
})

export const atualizarCampoSchema = z.object({
  nome: textoObrigatorio('nome do campo', 80).optional(),
  obrigatorio: z.boolean().optional(),
  opcoes: opcoesSchema.optional(),
  ordem: z.number().int().min(0).optional(),
  ativo: z.boolean().optional(),
})

export function precisaDeOpcoes(tipo: TipoCampo): boolean {
  return tipo === 'lista' || tipo === 'multipla_escolha'
}

export interface CampoDto {
  id: string
  nome: string
  chave: string
  tipo: TipoCampo
  entidade: EntidadeCampo
  obrigatorio: boolean
  opcoes: string[] | null
  ordem: number
  ativo: boolean
}
